<template id="message-preview-card">
    <div
        :class="['notification-item', {'cursor-pointer message-item-card' : canExpandMessage, 'selected' : message.isExpanded && canExpandMessage}]"
        @click.prevent="emitToggleExpanded(message)"
    >
        <!-- Header row section -->
        <div class="flex items-center gap-3">
            <div>
                <i v-if="message.is_new" class="fa-solid fa-circle notification-item-new-dot"></i>
                <span class="font-bold">{{ message.client.name }} </span>
            </div>

            <span class="text-muted text-sm">{{ processSentTime(message.created_at.date) }}</span>
            <i v-if="canExpandMessage" :class="'fa-regular fa-sm ' + getChevronClass(message)"></i>
        </div>

        <!-- Client message section -->
        <p ref="textElement" :class="['mb-0', {'truncate' : !message.isExpanded}]" v-sanitize-html="message.truncatedItem"></p>

        <!-- Attachments section -->
        <div v-if="showAttachments">
            <div v-for="file in getFirstThreeFiles(message.files)" :key="file.uuid" class="truncate">
                <i class="fa-light fa-paperclip-vertical mr-0"></i>
                <span class="underline"> {{ file.file_name }} </span>
            </div>

            <div v-if="Object.keys(message.files).length > 3">...</div>
        </div>

        <!-- Reply to message section -->
        <div v-if="canEditClient && message.isExpanded" @click.stop="">
            <form :id="`dropdown-send-message-form-${message.id}`" class="m-t-10 m-b-5 flex gap-5"  @submit.prevent="sendMessagePrompt(message)">
                <div class="input-group flex-1" :class="{ 'has-error' : errors.has('text') || errors.has('file') }">
                    <div class="w-full">
                        <textarea-autosize
                            @keydown="sendShortCut(message, $event)"
                            :placeholder="trans('apps.messaging_type_your_message')"
                            class="form-control"
                            name="text"
                            v-model="messageText"
                            id="messageInputField"
                            style="height: 28px;"
                            maxHeight="150px"
                            required>
                        </textarea-autosize>
                        <p v-show="errors.has('text')" class="text-danger mb-0">{{ errors.first('text') }}</p>
                    </div>
                </div>
                <button class="btn btn-custom h-full rounded-md" type="submit" :disabled="buttonLoading || isMessageSizeAboveLimit || !canSend()">
                    <i class="fa fa-send"></i>
                </button>
            </form>
        </div>

        <!-- url section -->
        <div class="notifications-item-links">
            <small class="related-url">
                <a v-if="canEditClient" class="m-r-10" @click.stop="emitToggleExpanded(message)"> {{ trans('messages.reply') }} </a>
                <a :href="`/app/messages-overview?clientId=${message.client.id}`" class="m-r-10" @click.stop=""> {{ trans('messages.view_message') }} </a>
                <a :href="`/app/client/${message.client.id}`" @click.stop="">{{ trans('clients.goto') }}</a>
            </small>
        </div>

    </div>
</template>

<script>
import TextareaAutosize from '@/system/components/global/form/TextareaAutosize';
import { http } from '../../../http';

export default {
    data() {
        return {
            messageText: "",
            buttonLoading: false,
            canBeExpanded: false
        }
    },

    props: {
        message: {
            required: true,
            type: Object
        },
    },

    components: {
        TextareaAutosize
    },

    mounted() {
        this.$nextTick(() => {
            this.setCanBeExpanded(this.$refs.textElement);
        });
    },

    methods: {
        //Handle message actions section
        emitToggleExpanded(message) {
            this.$emit('toggle-expand', message);
        },

        //Format message section
        processSentTime(date) {
            return moment(date).format('HH:mm');
        },

        getChevronClass(message) {
            return message.isExpanded ? 'fa-chevron-down': 'fa-chevron-up';
        },

        getFirstThreeFiles(filesObject) {
            return Object.keys(filesObject).slice(0, 3).map(key => filesObject[key]);
        },

        //Send new message section
        sendShortCut(message, e) {
            if(e.keyCode === 13 && (e.metaKey || e.ctrlKey) && this.canSend()) {
                this.sendMessagePrompt(message);
                e.preventDefault();
            }
        },

        canSend() {
            return this.messageText.trim().length > 0;
        },

        sendMessagePrompt(message) {
            if (this.isMessageSizeAboveLimit) {
                return;
            }

            if (!message.client.messaging_email && !message.client.messaging_sms) {
                swal({
                    title: trans('client.popup_info_title'),
                    text: trans('client.popup_info_text'),
                    type: "warning",
                    showCancelButton: true,
                    cancelButtonText: trans('global.cancel'),
                }, (confirmed) => {
                    if (confirmed) {
                        this.sendMessage(message);
                    }
                });
            } else {
                this.sendMessage(message);
            }
        },

        sendMessage(message) {
            let formData = new FormData();
            formData.append('is_from_user', 1);
            formData.append('client_id', message.client.id);
            formData.append('text', this.messageText);

            this.buttonLoading = true;

            http.post(`/app/client/${message.client.id}/messaging/send`, formData)
                .then(({ data }) => {
                    this.handleSendSuccess(message);
                    toastr.success(trans('messages.message_successfully_sent'));
                })
                .catch(({ error, errorBag }) => {
                    this.buttonLoading = false;
                    this.$setErrorBag(errorBag);
                    throw error; //re-throw error so that New Relic will pick it up.
                });
        },

        handleSendSuccess(message) {
            this.resetForm();
            this.buttonLoading = false;


            if (window.location.href.includes('/app/client/') || window.location.href.includes('/app/messages-overview')) {
                this.$event_bus.$emit('load-secure-messages');
            }

            this.$emit('remove-all-messages-for-client', message.client.id);
            this.$event_bus.$emit('remove-from-envelope-message-count', message.unread_messages_count);

        },

        resetForm() {
            this.errors.clear();
            this.messageText = '';
        },

        setCanBeExpanded(textContainer) {
            if(textContainer.scrollWidth > textContainer.offsetWidth) {
                this.canBeExpanded = true
            }
        },
    },

    computed: {
        canEditClient() {
            return this.$can('edit-client', this.message.client);
        },

        isMessageSizeAboveLimit() {
            const limit = 10 * 1024 * 1024; // 10 MB

            return this.messageText.length > limit;
        },

        showAttachments() {
            return this.message.has_file && (this.message.isExpanded || this.message.text==="");
        },

        canExpandMessage() {
            return this.canEditClient || this.canBeExpanded;
        },
    }
}
</script>
